<!--
    Notification Layout

    <NotificationLayout
      :notification="..."           # MANDATORY - notification object
      :notification-config="..."    # MANDATORY - config for notif type

      @click-trash - Fired on click on trash icon
    </NotificationLayout>
-->
<script setup lang="ts">
/* CONFIGS */
import { ALButton, ALIconButton } from '@af-lib/vue-ui';
import { computed, ref } from 'vue';

/* COMPONENTS */
import { displayGenericErrorToaster, displayGenericSuccessToaster } from '@/api/helpers/helpers';
import { t } from '@/config/i18n';
/* HELPERS */
import { getTimeDifferenceText } from '@/helpers/generic';
/* TYPES*/
import type { MeNotifications } from '@/types/api/me/me';
import type { NotificationConfig } from '@/types/notifications/notification';

/* PROPS */
type Props = {
  // Notification data from API
  notification: MeNotifications;
  notificationConfig: NotificationConfig;
};
const props = defineProps<Props>();

/* EMITS */
const emits = defineEmits<{
  (e: 'click:trash'): void;
}>();

/* CONSTANTS */
const isNew = props.notification.status === 'unread';

/* COMPUTED */
const isNotifRead = computed(() => {
  return props.notification.status === 'clicked';
});

/* ACTION (CTA button inside notification) */
const isActionLoading = ref(false);

async function onActionClick() {
  const action = props.notificationConfig.action;
  if (!action) return;

  isActionLoading.value = true;
  try {
    await action.handler(props.notification);
    displayGenericSuccessToaster(t('general.export_panel.success'));
    // On successful action, remove the notification from the list
    emits('click:trash');
  } catch (error) {
    console.error(error);
    displayGenericErrorToaster();
  } finally {
    isActionLoading.value = false;
  }
}
</script>

<template>
  <div v-if="notificationConfig" class="notification-container mb-2 p-4" :class="[{ new: isNew }]">
    <!-- Notification header with status and time -->
    <div class="mb-4 flex justify-between">
      <p class="text-body4-regular" :class="isNotifRead ? 'text-text-default' : 'text-text-quaternary'">
        {{ t(isNotifRead ? 'notifications.status.read' : 'notifications.status.unread').toUpperCase() }}
      </p>
      <p class="text-body4-light text-text-default">
        {{ getTimeDifferenceText(props.notification.created_at) }}
      </p>
    </div>
    <!-- Notification content  -->
    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <!-- Notification bubble icon -->
        <div
          class="me-4 flex size-10 shrink-0 items-center justify-center rounded-full"
          :class="notificationConfig.color(props.notification)"
        >
          <FaIcon
            :icon="notificationConfig.class(props.notification)"
            class="text-base text-icon-reverse"
          />
        </div>
        <!-- Notification text and trash icon -->
        <div class="flex flex-col gap-1">
          <p class="text-body3-light max-w-78 grow-0 text-text-default">
            {{ notificationConfig.translationKey(props.notification) }}
          </p>
          <!-- Optional action button -->
          <ALButton
            v-if="notificationConfig.action"
            size="s"
            type="tertiary"
            class="w-min"
            :label="notificationConfig.action.label(props.notification)"
            :icon="notificationConfig.action?.icon"
            :isLoading="isActionLoading"
            @click.stop="onActionClick"
          />
        </div>
      </div>
      <div class="self-start" @click.stop>
        <ALIconButton
          variant="tertiary"
          size="s"
          icon="fa fa-trash"
          :label="t('general.delete')"
          @click="emits('click:trash')"
        />
      </div>
    </div>
  </div>
</template>
